<script>
import getInitials from '@/helpers/get-initials'
import api from '@/helpers/api'
import { required, email, helpers, minLength } from 'vuelidate/lib/validators'

export default {
  name: 'customer.info',
  props: {
    customer_id: { type: String, required: true }
  },
  data: () => ({
    isLoading: false,
    customer: {
      billing_address: {}
    }
  }),
  mounted () {
    this.onInit()
  },
  watch: {
    customer_id: { handler: 'onInit', immediate: true }
  },
  validations: {
    customer: {
      legal_name: {
        required
      },
      vat_number: {
        minLength: minLength(1)
      },
      primary_email: {
        email
      }
    }
  },
  methods: {
    async onValidateVatNumberHandler () {
      let {
        valid,
        company_name,
        vat_number
      } = await api.customers.validateVatNumber({
        vat_number: this.customer.vat_number,
        customer_id: this.customer_id
      })

      this.customer.vat_number_valid = valid
      this.customer.vat_number_last_checked_at = new Date()
      if (valid) {
        this.$bvModal
          .msgBoxOk(
            `VAT Number ${vat_number} is ${valid ? 'valid' : 'Invalid'}`,
            {
              title: 'Validate VAT Number',
              size: 'sm',
              buttonSize: 'sm',
              okVariant: 'primary',
              headerClass: 'p-2 border-bottom-0',
              footerClass: 'p-2 border-top-0',
              centered: true
            }
          )
          .then(value => {})
          .catch(err => {
            // An error occurred
          })
      } else {
        this.$bvModal
          .msgBoxOk(
            `VAT Number ${vat_number} is ${valid ? 'valid' : 'Invalid'}`,
            {
              title: 'Validate VAT Number',
              size: 'sm',
              buttonSize: 'sm',
              okVariant: 'primary',
              headerClass: 'p-2 border-bottom-0',
              footerClass: 'p-2 border-top-0',
              centered: true
            }
          )
          .then(value => {})
          .catch(err => {
            // An error occurred
          })
      }
    },
    async onInit () {
      this.isLoading = true
      let item = await api.customers.fetch({
        id: this.customer_id
      })
      this.customer = {
        ...item
      }
      this.isLoading = false
    },
    async onSave () {
      let valid = true

      if (valid) {
        const data = {
          ...this.customer
        }
        const {
          attn,
          line1,
          line2,
          zipcode,
          city,
          country_code
        } = this.customer.billing_address
        try {
          this.isLoading = true
          let address = await api.addresses.update({
            id: this.customer.billing_address_id,
            attn,
            line1,
            line2,
            zipcode,
            city,
            country_code
          })
          let item = await api.customers.update({
            ...data,
            id: this.customer_id
          })

          this.$bvToast.toast(`Customer info was saved!`, {
            title: 'Changes Saved',
            solid: true,
            toaster: 'b-toaster-top-center',
            variant: 'success',
            autoHideDelay: 1000,
            appendToast: true
          })
          this.isLoading = false
        } catch (error) {
          this.isLoading = false

          this.$bvToast.toast(`${error}!`, {
            title: 'Error',
            solid: true,
            toaster: 'b-toaster-top-center',
            variant: 'danger',
            autoHideDelay: 1000,
            appendToast: true
          })
        }
      }
    }
  }
}
</script>
<template>
  <g-overlay :show="isLoading">
    <b-card>
      <h4 class="card-title">Basic Information</h4>
      <b-form-group
        label="Customer N°"
        label-for="customer_number"
        label-cols-sm="4"
        label-cols-lg="3"
      >
        <b-form-input
          id="customer_number"
          v-model="customer.customer_number"
          type="text"
          required
          readonly
          :style="{ width: '150px' }"
        ></b-form-input>
      </b-form-group>
      <b-form-group
        label="VAT N°"
        label-for="vat_number"
        label-cols-sm="4"
        label-cols-lg="3"
      >
        <b-input-group :style="{ width: '350px' }">
          <b-form-input
            id="vat_number"
            v-model="$v.customer.vat_number.$model"
            type="text"
            required
            autocomplete="vat_number"
            placeholder="Ex. BE27897439743"
          ></b-form-input>
          <b-input-group-append
            is-text
            class="bg-transparent"
            v-if="customer.vat_number_valid"
          >
            <i
              class="bx bx-check-shield text-success"
              id="tooltip-valid-vatnumber"
            ></i>
            <b-tooltip target="tooltip-valid-vatnumber" triggers="hover">
              Valid Vat Number<br />
              <small v-if="customer.vat_number_last_checked_at">
                Last checked
                {{
                  customer.vat_number_last_checked_at
                    | moment('DD/MM/YYYY -HH:mm')
                }}</small
              >
            </b-tooltip>
          </b-input-group-append>
          <b-input-group-append is-text class="bg-transparent" v-else>
            <i
              class="bx bx-x-circle text-danger"
              id="tooltip-valid-vatnumber"
            ></i>
            <b-tooltip target="tooltip-valid-vatnumber" triggers="hover">
              Invalid Vat Number<br />
              <small v-if="customer.vat_number_last_checked_at">
                Last checked
                {{
                  customer.vat_number_last_checked_at
                    | moment('DD/MM/YYYY -HH:mm')
                }}</small
              >
            </b-tooltip>
          </b-input-group-append>
          <b-input-group-append>
            <b-btn
              v-if="$v.customer.vat_number.$dirty"
              variant="outline-primary"
              v-on:click="onValidateVatNumberHandler"
              ><i class="bx bx-check mr-1"></i> Check</b-btn
            >
          </b-input-group-append>
        </b-input-group>
      </b-form-group>
      <b-form-group
        label="Legal Name"
        label-for="legal_name"
        label-cols-sm="4"
        label-cols-lg="3"
      >
        <b-form-input
          id="legal_name"
          v-model="customer.legal_name"
          type="text"
          required
          autocomplete="legal_name"
          placeholder="Legal name of the company"
          :style="{ width: '350px' }"
        ></b-form-input>
      </b-form-group>

      <b-form-group
        label="Email"
        label-for="primary_email"
        label-cols-sm="4"
        label-cols-lg="3"
      >
        <b-form-input
          id="primary_email"
          v-model="customer.primary_email"
          type="email"
          required
          autocomplete="new--password"
          placeholder="Primary Email address"
          :style="{ width: '350px' }"
        ></b-form-input>
      </b-form-group>
      <b-form-group
        label="Phone"
        label-for="primary_phone"
        label-cols-sm="4"
        label-cols-lg="3"
      >
        <b-form-input
          id="primary_phone"
          v-model="customer.primary_phone"
          type="text"
          required
          autocomplete="primary_phone"
          placeholder="ex. +32 4565663"
          :style="{ width: '350px' }"
        ></b-form-input>
      </b-form-group>

      <h4 class="card-title">Billing Address</h4>

      <b-form-group
        label="Attn."
        label-for="attn"
        label-cols-sm="4"
        label-cols-lg="3"
      >
        <b-form-input
          id="line1"
          v-model="customer.billing_address.attn"
          type="text"
          placeholder="Name of Person or Department"
          autocomplete="attn"
          :style="{ width: '350px' }"
        ></b-form-input>
      </b-form-group>
      <b-form-group
        label="Addressline 1"
        label-for="line1"
        label-cols-sm="4"
        label-cols-lg="3"
      >
        <b-form-input
          id="line1"
          v-model="customer.billing_address.line1"
          type="text"
          required
          autocomplete="addressline1"
          placeholder="Street and number"
          :style="{ width: '450px' }"
        ></b-form-input>
      </b-form-group>
      <b-form-group
        label="Addressline 2"
        label-for="line2"
        label-cols-sm="4"
        label-cols-lg="3"
      >
        <b-form-input
          id="line2"
          v-model="customer.billing_address.line2"
          type="text"
          autocomplete="addressline2"
          placeholder="Additional info ex. bus, floor, department"
          :style="{ width: '450px' }"
        ></b-form-input>
      </b-form-group>

      <b-form-group
        label="Zipcode"
        label-for="zipcode"
        label-cols-sm="4"
        label-cols-lg="3"
      >
        <b-form-input
          id="zipcode"
          v-model="customer.billing_address.zipcode"
          type="text"
          required
          autocomplete="zipcode"
          placeholder="Zipcode"
          :style="{ width: '150px' }"
        ></b-form-input>
      </b-form-group>

      <b-form-group
        label="City"
        label-for="city"
        label-cols-sm="4"
        label-cols-lg="3"
      >
        <b-form-input
          id="city"
          v-model="customer.billing_address.city"
          type="text"
          required
          autocomplete="city"
          placeholder="City"
          :style="{ width: '280px' }"
        ></b-form-input>
      </b-form-group>
      <b-form-group
        label="Country"
        label-for="country_code"
        label-cols-sm="4"
        label-cols-lg="3"
      >
        <app-country-select
          id="country_code"
          v-model="customer.billing_address.country_code"
          :style="{ width: '50%' }"
        />
      </b-form-group>

      <div class="d-flex justify-content-end">
        <b-btn class="btn-label" variant="primary" v-on:click="onSave">
          <i class="bx bx-save label-icon"></i>Save Changes
        </b-btn>
      </div>
    </b-card>
  </g-overlay>
</template>
